import * as React from 'react';
import {providers, client} from '@logilab/cwclientlibjs';
import {useQuery} from './useQuery';
import {MonacoToken} from './QueryInput';
import {useSearchBar} from './schema-navigator/SearchBar';

export interface RqlBrowserProps {
    schemaProvider: providers.RqlEntitySchemasLoader | null;
    clientRql: client.CwRqlClient;
    readonly?: boolean;
}

export function useRqlBrowser({clientRql, readonly}: RqlBrowserProps) {
    const {
        query,
        setQuery,
        resultsAndVariables,
        executeQuery,
        queryIsLoading,
        parser,
        isForbiddenQuery,
    } = useQuery({rqlClient: clientRql, readonly: readonly ?? false});
    const [error, setError] = React.useState<string | null>(null);
    const [showMetamodel, setMetamodel] = React.useState(false);
    const [cursorToken, setCursorToken] = React.useState<MonacoToken>({
        type: null,
        word: null,
    });

    const [autoHelp, setAutoHelp] = React.useState(false);

    const {
        textSearch: entityTypeTextSearch,
        setTextSearch: setEntityTypeTextSearch,
    } = useSearchBar();

    const {
        textSearch: relationsAndAttributesTextSearch,
        setTextSearch: setRelationsAndAttributesTextSearch,
    } = useSearchBar();

    const {
        textSearch: linkedEntityTypeTextSearch,
        setTextSearch: setLinkedEntityTypeTextSearch,
    } = useSearchBar();

    React.useEffect(() => {
        if (autoHelp) {
            if (cursorToken.type === 'identifier.relation.rql') {
                // Update relation and attributes search-bar
                if (cursorToken.word !== null && cursorToken.word.length > 1) {
                    setRelationsAndAttributesTextSearch(cursorToken.word);
                } else {
                    setRelationsAndAttributesTextSearch('');
                }
                setEntityTypeTextSearch('');
            } else if (cursorToken.type === 'identifier.entity-type.rql') {
                // Update entity-types search-bar
                setEntityTypeTextSearch(
                    cursorToken.word ? cursorToken.word : ''
                );
                setRelationsAndAttributesTextSearch('');
            } else if (cursorToken.type === 'rql.keyword.rql') {
                // Reset both search bars
                setEntityTypeTextSearch('');
                setRelationsAndAttributesTextSearch('');
            } else if (cursorToken.type === 'variable.rql') {
                // Look for a variable type in query
                const variableType = parser.getVariableType(
                    cursorToken.word ? cursorToken.word : ''
                );
                // if a type is found,
                if (variableType !== null) {
                    //reset relation search bar
                    setRelationsAndAttributesTextSearch('');
                    // setEntityTypeTextSearch to the type
                    setEntityTypeTextSearch(variableType);
                } else {
                    setRelationsAndAttributesTextSearch('');
                    setEntityTypeTextSearch('');
                }
            }
        }
    }, [
        cursorToken,
        autoHelp,
        setRelationsAndAttributesTextSearch,
        setEntityTypeTextSearch,
        parser,
    ]);

    return {
        autoHelp,
        setAutoHelp,
        showMetamodel,
        setMetamodel,
        entityTypeTextSearch,
        setEntityTypeTextSearch,
        relationsAndAttributesTextSearch,
        setRelationsAndAttributesTextSearch,
        linkedEntityTypeTextSearch,
        setLinkedEntityTypeTextSearch,
        error,
        setError,
        query,
        setQuery,
        executeQuery,
        resultsAndVariables,
        queryIsLoading,
        setCursorToken,
        isForbiddenQuery,
    };
}
